import { rewriteStaticAssetUrl } from '@/lib/static-asset-url';

export type HeroSlide = {
  title?: string;
  url?: string;
  width?: number;
  height?: number;
  id: number;
  original: string;
  thumbnail?: string;
};

export type HeroBreakpointParams = {
  breakpoint?: string;
  enabled?: boolean;
  selectedMedia?: Array<number | string>;
};

export type HeroSliderParams = {
  slider?: HeroBreakpointParams[];
};

/** Min viewport width per CMS breakpoint; keep in sync with `getClosestBreakpoint`. */
const BREAKPOINT_MIN_WIDTH: Record<string, number> = {
  xs: 0,
  sm: 376,
  md: 641,
  lg: 769,
  xl: 1025,
};

/** Banner sizes the CMS uses when attachments come without dimensions. */
const DEFAULT_MOBILE_SIZE = { width: 690, height: 356 };
const DEFAULT_DESKTOP_SIZE = { width: 1442, height: 409 };

export function defaultHeroSize(breakpoint?: string | null) {
  return breakpoint === 'xs' || breakpoint === 'sm'
    ? DEFAULT_MOBILE_SIZE
    : DEFAULT_DESKTOP_SIZE;
}

export function heroSlideSize(slide: HeroSlide, breakpoint?: string | null) {
  if (slide.width && slide.height) {
    return { width: slide.width, height: slide.height };
  }
  return defaultHeroSize(breakpoint);
}

export type HeroSource = {
  src: string;
  alt: string;
  width: number;
  height: number;
  /** Mutually exclusive viewport range; undefined when there is a single source. */
  media?: string;
};

/**
 * First slide per breakpoint, ordered from the narrowest range to the widest.
 * Returns `[]` when the server cannot reproduce what the client slider will show
 * (e.g. a disabled breakpoint), so nothing wrong is ever painted.
 */
export function resolveHeroSources({
  sliders,
  params,
  renderByBreakpoint,
}: {
  sliders?: HeroSlide[];
  params?: HeroSliderParams;
  renderByBreakpoint: boolean;
}): HeroSource[] {
  const files = (sliders || []).filter((file) => file?.original);
  if (!files.length) return [];

  if (!renderByBreakpoint) {
    const first = files[0];
    // Prefer CMS dimensions; fall back to default banner size so SSR can still
    // reserve aspect-ratio space (CLS) when attachments omit width/height.
    const size = heroSlideSize(first, null);
    return [
      {
        src: rewriteStaticAssetUrl(first.original),
        alt: first.title || '',
        width: size.width,
        height: size.height,
      },
    ];
  }

  const breakpoints = (params?.slider || [])
    .filter(
      (bp): bp is HeroBreakpointParams & { breakpoint: string } =>
        typeof bp?.breakpoint === 'string' && bp.breakpoint in BREAKPOINT_MIN_WIDTH,
    )
    .sort(
      (a, b) =>
        BREAKPOINT_MIN_WIDTH[a.breakpoint] - BREAKPOINT_MIN_WIDTH[b.breakpoint],
    );
  if (!breakpoints.length || breakpoints.some((bp) => !bp.enabled)) return [];

  const sources: HeroSource[] = [];
  for (const [index, bp] of breakpoints.entries()) {
    const selected = bp.selectedMedia?.length
      ? bp.selectedMedia
          .map((id) => files.find((file) => file.id === id))
          .find(Boolean)
      : files[0];
    if (!selected) return [];

    const next = breakpoints[index + 1];
    const min = index === 0 ? 0 : BREAKPOINT_MIN_WIDTH[bp.breakpoint];
    const max = next ? BREAKPOINT_MIN_WIDTH[next.breakpoint] - 1 : undefined;
    const conditions = [
      min > 0 ? `(min-width: ${min}px)` : null,
      max !== undefined ? `(max-width: ${max}px)` : null,
    ].filter(Boolean);

    sources.push({
      src: rewriteStaticAssetUrl(selected.original),
      alt: selected.title || '',
      ...heroSlideSize(selected, bp.breakpoint),
      media: conditions.length ? conditions.join(' and ') : undefined,
    });
  }
  return sources;
}
